import "./SettingsPage.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { KaboodleToaster } from "components/common";
import { TextInput } from "components/inputs";
import QuickBooksOAuth from "components/Invoicing/QuickBooks/QuickBooksOAuth";
import KaboodleModal from "components/Modals/KaboodleModal";
import { LogoUploader } from "components/OrganizationLogo/LogoUploader";
import OrganizationLogo from "components/OrganizationLogo/OrganizationLogo";
import SectionBreak from "components/SectionBreak";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useDeleteOrganizationLetterheadMutation,
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation,
} from "services/misc-api";

import AcknowledgementLetterContactsSection from "./AcknowledgementLetterContactsSection";
import OrganizationAddressSection from "./OrganizationAddressSection";
import OrganizationUsersTable from "./OrganizationUsersTable";
import SettingsPageContainer from "./SettingsPageContainer";

export type OrganizationSettingsFormValues = {
  name: string;
  legal_name: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip_code: string;
  claim_number_prefix: string;
  msa_acknowledgement_contact_title: string;
  msa_acknowledgement_contact_name: string;
  msa_acknowledgement_contact_phone: string;
  mcp_acknowledgement_contact_title: string;
  mcp_acknowledgement_contact_name: string;
  mcp_acknowledgement_contact_phone: string;
};

const QuickbooksAuth = () => {
  return <QuickBooksOAuth />;
};

const UserManagement = () => {
  return (
    <div className="user-management">
      <OrganizationUsersTable />
    </div>
  );
};

const OrganizationBranding = () => {
  const { data: orgSettings } = useGetOrganizationSettingsQuery();
  const [deleteLetterhead, { isLoading: isDeleting }] =
    useDeleteOrganizationLetterheadMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const hasLogo = Boolean(orgSettings?.document_letterhead_s3_key);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteLetterhead().unwrap();
      KaboodleToaster.success("Logo deleted successfully");
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Failed to delete logo:", error);
      KaboodleToaster.error("Failed to delete logo. Please try again.");
    }
  };

  const deleteModalFooter = (
    <div className="flex jc-c">
      <Button
        className="delete"
        onClick={handleDeleteConfirm}
        disabled={isDeleting}
      >
        {isDeleting ? "Deleting..." : "Confirm Delete"}
      </Button>
      <Button onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
        Cancel
      </Button>
    </div>
  );

  return (
    <Card heading="ORGANIZATION LOGO & COMPANY LETTERHEAD">
      <div className="organization-branding flex jc-sb ai-c">
        <div className="organization-branding__section">
          <p className="text-secondary mb-4">
            This logo will appear as your organization letterhead in documents.
            Upload a high-quality PNG or JPG file (max 5MB).
          </p>

          <div className="letterhead-preview-section">
            <div className="flex jc-sb ai-c mb-2">
              <h4 className="letterhead-preview-title">
                Current Letterhead Logo
              </h4>
              {hasLogo && (
                <Button
                  onClick={handleDeleteClick}
                  className="btn-danger btn-sm"
                  disabled={isDeleting}
                >
                  Delete Logo
                </Button>
              )}
            </div>
            <OrganizationLogo />
          </div>

          <h4 className="letterhead-upload-title mt-4 mb-2">Upload New Logo</h4>
          <LogoUploader />
        </div>
      </div>

      <KaboodleModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        footer={deleteModalFooter}
      >
        <Card heading="Delete Organization Logo">
          <p>
            Are you sure you want to delete your organization's letterhead logo?
          </p>
          <p>
            This action cannot be undone. The logo will be removed from all
            future documents.
          </p>
        </Card>
      </KaboodleModal>
    </Card>
  );
};

const OrganizationSettings = () => {
  const { data: organizationData, isLoading } =
    useGetOrganizationSettingsQuery();
  const [updateOrganizationSettings] = useUpdateOrganizationSettingsMutation();

  const { control, reset } = useForm<OrganizationSettingsFormValues>({
    defaultValues: {
      name: "",
      legal_name: "",
      street: "",
      street2: "",
      city: "",
      state: "",
      zip_code: "",
      claim_number_prefix: "",
      msa_acknowledgement_contact_title: "",
      msa_acknowledgement_contact_name: "",
      msa_acknowledgement_contact_phone: "",
      mcp_acknowledgement_contact_title: "",
      mcp_acknowledgement_contact_name: "",
      mcp_acknowledgement_contact_phone: "",
    },
  });

  useEffect(() => {
    if (organizationData) {
      reset(
        {
          name: organizationData.name || "",
          legal_name: organizationData.legal_name || "",
          street: organizationData.street || "",
          street2: organizationData.street2 || "",
          city: organizationData.city || "",
          state: organizationData.state || "",
          zip_code: organizationData.zip_code || "",
          claim_number_prefix: organizationData.claim_number_prefix || "",
          msa_acknowledgement_contact_title:
            organizationData.msa_acknowledgement_contact_title || "",
          msa_acknowledgement_contact_name:
            organizationData.msa_acknowledgement_contact_name || "",
          msa_acknowledgement_contact_phone:
            organizationData.msa_acknowledgement_contact_phone || "",
          mcp_acknowledgement_contact_title:
            organizationData.mcp_acknowledgement_contact_title || "",
          mcp_acknowledgement_contact_name:
            organizationData.mcp_acknowledgement_contact_name || "",
          mcp_acknowledgement_contact_phone:
            organizationData.mcp_acknowledgement_contact_phone || "",
        },
        { keepDirtyValues: true },
      );
    }
  }, [organizationData, reset]);

  const handleFieldChange = async (
    field: keyof OrganizationSettingsFormValues,
    value: string,
  ) => {
    try {
      await updateOrganizationSettings({
        data: { [field]: value },
      }).unwrap();
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <FutureMedicalCard heading="ORGANIZATION SETTINGS">
      <div className="header-primary flex jc-sb ai-c">
        <div className="text-sm text-gray-500">Basic Information</div>
      </div>

      <ControlledInput
        control={control}
        name="name"
        label="Organization Name"
        errors={{}}
        onSave={(value) => handleFieldChange("name", String(value))}
        componentType={TextInput}
        maxLength={255}
      />

      <SectionBreak />

      <OrganizationAddressSection
        control={control}
        handleFieldChange={handleFieldChange}
      />

      <SectionBreak />

      <div className="header-primary flex jc-sb ai-c">
        <div className="text-sm text-gray-500">Additional Settings</div>
      </div>

      <ControlledInput
        control={control}
        name="claim_number_prefix"
        label="Claim Number Prefix"
        errors={{}}
        onSave={(value) =>
          handleFieldChange("claim_number_prefix", String(value))
        }
        componentType={TextInput}
        maxLength={255}
      />

      <SectionBreak />

      <AcknowledgementLetterContactsSection
        control={control}
        handleFieldChange={handleFieldChange}
      />
    </FutureMedicalCard>
  );
};

const SettingsPage = () => {
  const { data: orgSettings } = useGetOrganizationSettingsQuery();

  const leftPanelContent = (
    <>
      <OrganizationSettings />
      {orgSettings?.quickbooks_integration_enabled ? <QuickbooksAuth /> : null}
    </>
  );

  const rightPanelContent = (
    <>
      <UserManagement />
      <OrganizationBranding />
    </>
  );

  return (
    <SettingsPageContainer
      childrenLeft={leftPanelContent}
      childrenRight={rightPanelContent}
    />
  );
};

export default SettingsPage;
